import {
  forwardRef,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

import NumberFormat from 'react-number-format';

import DarkModeIcon from '@mui/icons-material/DarkMode';
import InfoIcon from '@mui/icons-material/Info';
import LightModeIcon from '@mui/icons-material/LightMode';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SettingsIcon from '@mui/icons-material/Settings';

import { ReactComponent as ActegonIcon } from './actegon.svg';

import Results from './Results';
import SettingsDialog, { DEFAULT_SETTINGS } from './SettingsDialog';
import TaxChart from './TaxChart';
import { ColorModeContext } from './ThemeRoot';
import { KATA_LIMIT, computeTaxes } from './taxes';
import {
  DEFAULT_FORM,
  IPA_KULCSOK,
  readForm,
  readSettings,
  writeUrl,
} from './urlState';

import './App.css';

// a táblázat és a grafikon közös színei és nevei, sötét módban a sötét
// háttérhez igazított árnyalatokkal
const SCHEMES = [
  { key: 'kata', label: 'KATA', short: 'KATA', colors: { light: '#2a78d6', dark: '#3987e5' } },
  { key: 'atalany', label: 'Átalányadózás', short: 'Átalányadó', colors: { light: '#eb6834', dark: '#d95926' } },
  { key: 'teteles', label: 'Tételes költségelszámolás', short: 'Tételes', colors: { light: '#1baf7a', dark: '#199e70' } },
  { key: 'kft', label: 'Kft (osztalék)', short: 'Kft', colors: { light: '#eda100', dark: '#c98500' } },
];

// mértékegységek eFt-ban kifejezve
const UNITS = { Ft: 0.001, eFt: 1, MFt: 1000 };
const UNIT_LABELS = { Ft: 'Ft', eFt: 'eFt', MFt: 'M Ft' };
const roundToUnit = (value, unit) =>
  unit === 'MFt' ? Math.round(value * 1000) / 1000 : Math.floor(value);

// az egyéni minimálbér böngészőnként megmarad, a linkben kapott érték felülírja
const SETTINGS_KEY = 'beallitasok';
const loadSettings = () => {
  let stored = {};
  try {
    stored = JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}');
  } catch {}
  return { ...DEFAULT_SETTINGS, ...stored, ...readSettings() };
};
const storeSettings = (settings) => {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {}
};

const initialForm = readForm();

function App() {
  const colorMode = useContext(ColorModeContext);
  const schemes = useMemo(
    () => SCHEMES.map((s) => ({ ...s, color: s.colors[colorMode.mode] })),
    [colorMode.mode]
  );
  const [income, setIncome] = useState(initialForm.income);
  const [expense, setExpense] = useState(initialForm.expense);
  const [unit, setUnit] = useState(initialForm.unit);
  const [koltseghanyad, setKoltseghanyad] = useState(initialForm.koltseghanyad);
  const [ipaKulcs, setIpaKulcs] = useState(initialForm.ipaKulcs);

  const [settings, setSettings] = useState(loadSettings);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { minimalber: _minimalber, berminimum: _berminimum } = settings;
  const customSettings =
    _minimalber !== DEFAULT_SETTINGS.minimalber ||
    _berminimum !== DEFAULT_SETTINGS.berminimum;
  const [garantalt, setGarantalt] = useState(initialForm.garantalt);
  const [allastipus, setAllastipus] = useState(initialForm.allastipus);
  const [monthly, setMonthly] = useState(initialForm.monthly);
  const [szjaMentes, setSzjaMentes] = useState(initialForm.szjaMentes);
  const [cegesJovedelem, setCegesJovedelem] = useState(
    initialForm.cegesJovedelem
  );

  const form = {
    income,
    expense,
    unit,
    koltseghanyad,
    ipaKulcs,
    allastipus,
    garantalt,
    monthly,
    szjaMentes,
    cegesJovedelem,
  };
  const formChanged = Object.keys(DEFAULT_FORM).some(
    (key) => form[key] !== DEFAULT_FORM[key]
  );
  const resetForm = () => {
    setIncome(DEFAULT_FORM.income);
    setExpense(DEFAULT_FORM.expense);
    setUnit(DEFAULT_FORM.unit);
    setKoltseghanyad(DEFAULT_FORM.koltseghanyad);
    setIpaKulcs(DEFAULT_FORM.ipaKulcs);
    setAllastipus(DEFAULT_FORM.allastipus);
    setGarantalt(DEFAULT_FORM.garantalt);
    setMonthly(DEFAULT_FORM.monthly);
    setSzjaMentes(DEFAULT_FORM.szjaMentes);
    setCegesJovedelem(DEFAULT_FORM.cegesJovedelem);
  };

  useEffect(() => {
    writeUrl(form, settings, DEFAULT_SETTINGS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...Object.values(form), settings]);

  const foallasu = allastipus === 'foallasu';
  const mobile = useMediaQuery('(max-width:599.95px)');

  const incomeInThousands = Number(income) * UNITS[unit];
  const expenseInThousands = Number(expense) * UNITS[unit];

  // érték átváltása másik mértékegységre, havi/éves váltáskor is
  const convert = (value, toUnit, factor = 1) =>
    String(roundToUnit((Number(value) * UNITS[unit] * factor) / UNITS[toUnit], toUnit));
  const changeUnit = (toUnit, factor = 1) => {
    setIncome(convert(income, toUnit, factor));
    setExpense(convert(expense, toUnit, factor));
    setUnit(toUnit);
  };

  // a számítás éves, havi nézetben minden tételt 12-vel osztunk
  const periods = monthly ? 12 : 1;
  const params = useMemo(
    () => ({
      koltseghanyad,
      ipaKulcs: ipaKulcs / 100,
      minimalber: _minimalber,
      jarulekMinimum: garantalt ? _berminimum : _minimalber,
      allastipus,
      szjaMentes,
      cegesJovedelem,
    }),
    [
      koltseghanyad,
      ipaKulcs,
      _minimalber,
      _berminimum,
      garantalt,
      allastipus,
      szjaMentes,
      cegesJovedelem,
    ]
  );
  const annualTaxes = computeTaxes({
    ...params,
    income: incomeInThousands * periods,
    expense: expenseInThousands * periods,
  });
  const taxes = Object.fromEntries(
    Object.entries(annualTaxes).map(([key, values]) => [
      key,
      Object.fromEntries(
        Object.entries(values).map(([k, v]) => [
          k,
          typeof v === 'number' ? v / periods : v,
        ])
      ),
    ])
  );

  const NumberFormatCustom = useMemo(
    () =>
      forwardRef(function NumberFormatCustom(props, ref) {
        const { onChange, ...other } = props;

        return (
          <NumberFormat
            {...other}
            getInputRef={ref}
            onValueChange={(values) => {
              onChange({
                target: {
                  name: props.name,
                  value: values.value,
                },
              });
            }}
            thousandSeparator=' '
            decimalSeparator=','
            isNumericString
            allowNegative={false}
            decimalScale={unit === 'MFt' ? 3 : 0}
          />
        );
      }),
    [unit]
  );

  const unitSelect = (
    <Select
      variant='standard'
      disableUnderline
      value={unit}
      onChange={(e) => changeUnit(e.target.value)}
    >
      {Object.keys(UNITS).map((u) => (
        <MenuItem key={u} value={u}>
          {UNIT_LABELS[u]}
        </MenuItem>
      ))}
    </Select>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        maxWidth: '100%',
        marginTop: { xs: 0, sm: 0, md: 2, lg: 2, xl: 2 },
        marginBottom: { xs: 0, sm: 0, md: 2, lg: 2, xl: 2 },
      }}
    >
      <Paper
        elevation={3}
        sx={{
          padding: { xs: 2, sm: 3 },
          width: 700,
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <Typography
              variant='h4'
              sx={{ fontSize: { xs: '1.6rem', sm: '2.125rem' }, flexGrow: 1 }}
            >
              KATA vs Átalányadó kalkulátor 2027
            </Typography>
            {formChanged ? (
              <Tooltip title='Alaphelyzet'>
                <IconButton
                  size='small'
                  aria-label='Alaphelyzet'
                  onClick={resetForm}
                  sx={{ color: 'action.active' }}
                >
                  <RestartAltIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            ) : null}
            <Tooltip
              title={colorMode.mode === 'dark' ? 'Világos mód' : 'Sötét mód'}
            >
              <IconButton
                size='small'
                aria-label={
                  colorMode.mode === 'dark' ? 'Világos mód' : 'Sötét mód'
                }
                onClick={colorMode.toggle}
                sx={{ color: 'action.active' }}
              >
                {colorMode.mode === 'dark' ? (
                  <LightModeIcon fontSize='small' />
                ) : (
                  <DarkModeIcon fontSize='small' />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip
              title={
                customSettings
                  ? 'Beállítások (egyéni minimálbér)'
                  : 'Beállítások'
              }
            >
              <IconButton
                size='small'
                aria-label='Beállítások'
                onClick={() => setSettingsOpen(true)}
                sx={{
                  color: customSettings ? 'primary.main' : 'action.disabled',
                }}
              >
                <SettingsIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </Box>
          <SettingsDialog
            open={settingsOpen}
            settings={settings}
            onClose={() => setSettingsOpen(false)}
            onSave={(next) => {
              setSettings(next);
              storeSettings(next);
              setSettingsOpen(false);
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={monthly}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setMonthly(checked);
                  // éves összeg alapból M Ft-ban, havi eFt-ban
                  const toUnit =
                    checked && unit === 'MFt'
                      ? 'eFt'
                      : !checked && unit === 'eFt'
                      ? 'MFt'
                      : unit;
                  changeUnit(toUnit, checked ? 1 / 12 : 12);
                }}
              />
            }
            label='Havi osztás'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={szjaMentes}
                onChange={(e) => setSzjaMentes(e.target.checked)}
              />
            }
            label='25 év alatti'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={cegesJovedelem}
                onChange={(e) => setCegesJovedelem(e.target.checked)}
              />
            }
            label='Céges jövedelem (KATA: 15%, a havi fix KATA beleszámít)'
          />
          <FormControl>
            <FormHelperText>
              {!monthly
                ? 'Mennyi az éves bevételed?'
                : 'Mennyi a havi bevételed?'}
            </FormHelperText>
            <TextField
              onChange={(e) => setIncome(e.target.value)}
              value={income}
              inputProps={{
                inputMode: unit === 'MFt' ? 'decimal' : 'numeric',
              }}
              InputProps={{
                inputComponent: NumberFormatCustom,
                endAdornment: (
                  <InputAdornment position='end'>
                    {unitSelect}
                    <Tooltip
                      title={`KATA esetén évi ${(KATA_LIMIT * 1000).toLocaleString('hu')} Ft felett büntetőadó. Átalányadó esetén maximum ${ (_minimalber * 120 * 1000).toLocaleString('hu')} Ft (90%-os költséghányad esetén ${(_minimalber * 600 * 1000).toLocaleString('hu')} Ft).`}
                    >
                      <IconButton>
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </FormControl>
          <FormControl>
            <FormHelperText>
              {!monthly
                ? 'Mennyi az éves költséged?'
                : 'Mennyi a havi költséged?'}
            </FormHelperText>
            <TextField
              onChange={(e) => setExpense(e.target.value)}
              value={expense}
              inputProps={{
                inputMode: unit === 'MFt' ? 'decimal' : 'numeric',
              }}
              InputProps={{
                inputComponent: NumberFormatCustom,
                endAdornment: (
                  <InputAdornment position='end'>{unitSelect}</InputAdornment>
                ),
                // endAdornment: (
                //   <InputAdornment position='end'>
                //     <Tooltip
                //       title={`KATA esetén 12.000.000 Ft felett büntetőadó. Átalányadó esetén maximum 24.000.000 Ft.`}
                //     >
                //       <IconButton>
                //         <InfoIcon />
                //       </IconButton>
                //     </Tooltip>
                //   </InputAdornment>
                // ),
              }}
            />
          </FormControl>
          <FormControl>
            <FormHelperText>
              Főállású, méllékállású vagy nyugdíjas egyéni vállalkozó vagy?
            </FormHelperText>
            <Select
              value={allastipus}
              className='andornment-select'
              onChange={(e) => setAllastipus(e.target.value)}
              endAdornment={
                <InputAdornment position='end' style={{}}>
                  <Tooltip
                    title={`Mellékállású egyéni vállalkozó: rendelkezik legalább 36 órás munkaviszonnyal VAGY nappali tagozaton tanul VAGY saját jogú nyugdíjas (kiegészítő tevékenységet folytat)`}
                  >
                    <IconButton>
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              }
            >
              <MenuItem value={'foallasu'}>Főállású</MenuItem>
              <MenuItem value={'mellekallasu'}>Mellékállású</MenuItem>
              <MenuItem value={'nyugdijas'}>Nyugdíjas</MenuItem>
            </Select>
          </FormControl>
          {foallasu ? (
            <FormControl>
              <FormHelperText>
                A tevékenység középiskolai végzettséghez, illetőleg
                szakképesítéshez van kötve?
              </FormHelperText>
              <Select
                value={garantalt}
                onChange={(e) => setGarantalt(e.target.value)}
                className='andornment-select'
                endAdornment={
                  <InputAdornment position='end' style={{}}>
                    <Tooltip
                      title={`Amennyiben főállású egyéni vállalkozóról van szó, akkor bevételtől függetlenül minden hónapban be kell fizetni a járulékokat (TB, SZOCHO) a minimálbér értéke után. Amennyiben tevékenység alapján emelt minimálbér (garantált bérminimum ${_berminimum.toLocaleString('hu')} ezer Ft) járna az alap minimálbér (${_minimalber.toLocaleString('hu')} ezer Ft) helyett, akkor a kalkulátor ez alapján számol. Az összegek a beállításokban (fogaskerék) módosíthatók.`}
                    >
                      <IconButton>
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                }
              >
                <MenuItem value={true}>Igen</MenuItem>
                <MenuItem value={false}>Nem</MenuItem>
              </Select>
            </FormControl>
          ) : null}
          <FormControl>
            <FormHelperText>
              Milyen költséghányadú tevékenységet végzel?
            </FormHelperText>
            <Select
              value={koltseghanyad}
              onChange={(e) => setKoltseghanyad(e.target.value)}
              className='andornment-select'
              endAdornment={
                <InputAdornment position='end' style={{}}>
                  <Tooltip
                    title={
                      <div>
                        <b>50%-os költséghányad</b>: Ez az alapeset, ezt nem
                        sorolja fel a törvény, ha nem férsz bele a 80%-os vagy a
                        90%-os költséghányadba, akkor a 50%-os költséghányad alá
                        tartozol.<br></br>
                        <b>80%-os költséghányad</b>: a) mezőgazdasági,
                        erdőgazdálkodási (TESZOR 01, 02), bányászati (TESZOR
                        05-től 09-ig) és feldolgozóipari (TESZOR 10-től 32-ig)
                        termék-előállítás, építőipari kivitelezés (TESZOR 41,
                        42); b) mezőgazdasági, betakarítást követő szolgáltatás
                        (TESZOR 01.6), vadgazdálkodáshoz kapcsolódó szolgáltatás
                        (TESZOR 01.70.10), erdészeti szolgáltatás (TESZOR
                        02.40.10) és zöldterület-kezelés (TESZOR 81.30.10); c)
                        halászati szolgáltatás (TESZOR 03.00.71),
                        halgazdálkodási szolgáltatás (TESZOR 03.00.72); d)
                        feldolgozóipari szolgáltatás (TESZOR 10-től 32-ig) a
                        bérmunkában végzett szolgáltatás és az egyéb
                        sokszorosítás (TESZOR 18.20) kivételével; e) építőipari
                        szolgáltatás (TESZOR 43); f) ipari gép, berendezés,
                        eszköz javítása (TESZOR 33.1), gépjárműjavítás (TESZOR
                        45.20), személyi, háztartási cikk javítása (TESZOR
                        95.2), épületgépészeti berendezések javítása (TESZOR
                        43.21, 43.22, 43.29); g) a taxis személyszállítás
                        (TESZOR 49.32.11) személygépjármű kölcsönzése vezetővel
                        (TESZOR 49.32.12), egyéb máshová nem sorolt szárazföldi
                        személyszállítás (TESZOR 49.39.39), közúti áruszállítás
                        (TESZOR 49.41.1); h) számítógép, kommunikációs eszköz
                        javítása (TESZOR 95.1); i) fényképészet (TESZOR 74.20);
                        j) textil, szőrme mosása, tisztítása (TESZOR 96.01),
                        fodrászat, szépségápolás (TESZOR 96.02),
                        hobbiállat-gondozás (TESZOR 96.09.11); k) a kereskedelmi
                        tevékenységek végzésének feltételeiről szóló
                        kormányrendelet alapján folytatott vendéglátó
                        tevékenység (TESZOR 56). és kiskereskedelmi
                        tevékenységből bevételt szerző egyéni vállalkozó. Ez azt
                        jelenti, hogy ha nem kizárólag kiskereskedelmi
                        tevékenységet végez, hanem mellette van másik
                        kedvezményes költséghányadú végzett tevékenység is,
                        akkor 80%-os költséghányadot lehet csak alkalmazni.
                        <br></br>
                        <b>90%-os költséghányad</b>: aki az adóév egészében
                        kizárólag kiskereskedelmi tevékenységből szerez bevételt
                        (azaz más tevékenységből nem lehet bevétele)
                        mezőgazdasági őstermelő.
                      </div>
                    }
                  >
                    <IconButton>
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              }
            >
              <MenuItem value={0.5}>50%</MenuItem>
              <MenuItem value={0.8}>80%</MenuItem>
              <MenuItem value={0.9}>90%</MenuItem>
            </Select>
          </FormControl>
          <FormControl>
            <FormHelperText>
              Mennyi az iparűzési adó a településeden?
            </FormHelperText>
            <Select
              value={ipaKulcs}
              onChange={(e) => setIpaKulcs(e.target.value)}
              className='andornment-select'
              endAdornment={
                <InputAdornment position='end' style={{}}>
                  <Tooltip
                    title={`A helyi iparűzési adó mértékét az önkormányzat határozza meg, legfeljebb 2%. A legtöbb településen 2%, a pontos értéket a székhelyed szerinti önkormányzat honlapján találod.`}
                  >
                    <IconButton>
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              }
            >
              {IPA_KULCSOK.map((k) => (
                <MenuItem key={k} value={k}>
                  {k.toLocaleString('hu-HU', { minimumFractionDigits: 1 })}%
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Results
          mobile={mobile}
          schemes={schemes}
          taxes={taxes}
          income={incomeInThousands}
          expense={expenseInThousands}
          koltseghanyad={koltseghanyad}
          ipaKulcs={ipaKulcs}
          cegesJovedelem={cegesJovedelem}
        />
        <TaxChart
          params={params}
          expenseRatio={
            incomeInThousands > 0
              ? Math.min(expenseInThousands / incomeInThousands, 1)
              : 0
          }
          currentIncome={incomeInThousands * periods}
          schemes={schemes}
          height={mobile ? 240 : 300}
        />
        <Box sx={{ marginTop: 1 }}>
          <Box
            component='a'
            href='https://actegon.com'
            target='_blank'
            rel='noreferrer'
            sx={{
              display: 'block',
              textAlign: 'center',
              marginTop: '15px',
              marginBottom: '10px',
              // a logó sötétkék felirata sötét módban a szövegszínt veszi fel
              '& [fill="#101733"]': { fill: (theme) => theme.palette.text.primary },
            }}
          >
            <ActegonIcon style={{ width: 180, height: 'auto' }} />
          </Box>
          <Typography variant='body1' gutterBottom>
            Kalkuláld ki, hogy KATA adózásról átalányadózásra vagy tételes
            költségelszámolásra váltva milyen opcióid vannak.
          </Typography>
          <Typography variant='body1' gutterBottom>
            Cégünk szoftverfejlesztéssel foglalkozik. Dinamikusan dolgozunk.
            Amennyiben úgy gondolod, hogy tudnánk együtt dolgozni, dobj egy
            e-mailt a <a href='mailto:hello@actegon.com'>hello@actegon.com</a>{' '}
            címre.
          </Typography>
          <Typography variant='body2' style={{ fontStyle: 'italic' }}>
            A kalkulátor megjelenített értékei tájékoztató jellegűek. Azokért
            felelősséget nem vállalunk.
          </Typography>
          <Divider sx={{ marginTop: 1, marginBottom: 1 }} />
          <Typography variant='overline'>
            Copyright © {new Date().getFullYear()} | Actegon Solutions Kft. All
            rights reserved.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}

export default App;
